import { useState, useEffect, useCallback, useMemo, useRef, RefObject } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  DocumentSnapshot,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { processTransaction, sortTransactionsByDate } from "../../../utils/transactionProcessing";
import { TRANSACTIONS_PAGE_SIZE } from "./useTransactions";

interface UsePaginationProps {
  workspaceId: string;
  transactions: TransactionWithDate[];
  hasMoreTransactions: boolean;
  lastTransactionDoc: DocumentSnapshot | null;
  isFullyLoaded: boolean;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  allTransactionsRef: React.MutableRefObject<TransactionWithDate[]>;
  filteredTransactionsLength?: number; // Just the length, not the full array to avoid circular dependency (optional, defaults to allTransactions.length)
}

export const usePagination = ({
  workspaceId,
  transactions,
  hasMoreTransactions,
  lastTransactionDoc,
  isFullyLoaded,
  tableContainerRef,
  allTransactionsRef,
  filteredTransactionsLength,
}: UsePaginationProps) => {
  const [displayedCount, setDisplayedCount] = useState<number>(TRANSACTIONS_PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [paginatedTransactions, setPaginatedTransactions] = useState<TransactionWithDate[]>([]);
  const [localHasMoreTransactions, setLocalHasMoreTransactions] = useState<boolean>(true);
  const [localLastTransactionDoc, setLocalLastTransactionDoc] = useState<DocumentSnapshot | null>(null);
  const loadingMoreRef = useRef<boolean>(false);

  // Merge hook transactions with paginated transactions (deduplicate by id to prevent duplicates from race conditions)
  const allTransactions = useMemo(() => {
    if (paginatedTransactions.length === 0) {
      return transactions;
    }
    const seenIds = new Set(transactions.map((t) => t.id));
    const uniquePaginated = paginatedTransactions.filter((t) => {
      if (seenIds.has(t.id)) return false;
      seenIds.add(t.id);
      return true;
    });
    const merged = [...transactions, ...uniquePaginated];
    return sortTransactionsByDate(merged);
  }, [transactions, paginatedTransactions]);

  // Update allTransactionsRef when transactions change
  useEffect(() => {
    allTransactionsRef.current = allTransactions;
  }, [allTransactions, allTransactionsRef]);

  // Load more transactions from Firestore
  const loadMoreTransactions = useCallback(async (): Promise<void> => {
    const docToUse = localLastTransactionDoc || lastTransactionDoc;
    const hasMore = localHasMoreTransactions && hasMoreTransactions;
    // Use ref for guard - prevents race when scroll fires before setLoadingMore(true) takes effect
    if (!workspaceId || !auth.currentUser || loadingMore || loadingMoreRef.current || !hasMore || !docToUse) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
      // Use date field for pagination to match the initial query ordering
      const nextQuery = query(
        transactionsRef,
        orderBy("date", "desc"),
        startAfter(docToUse),
        limit(TRANSACTIONS_PAGE_SIZE)
      );

      const snapshot = await getDocs(nextQuery);
      
      if (snapshot.empty) {
        setLocalHasMoreTransactions(false);
        loadingMoreRef.current = false;
        setLoadingMore(false);
        return;
      }

      // Process transactions using utility function
      const newTransactions: TransactionWithDate[] = snapshot.docs.map(processTransaction);
      
      // Add to paginated transactions (deduplicate by id to prevent duplicates from rapid scroll/race)
      setPaginatedTransactions((prev) => {
        const seenIds = new Set(prev.map((t) => t.id));
        const uniqueNew = newTransactions.filter((t) => {
          if (seenIds.has(t.id)) return false;
          seenIds.add(t.id);
          return true;
        });
        const merged = [...prev, ...uniqueNew];
        return sortTransactionsByDate(merged);
      });
      
      if (snapshot.docs.length < TRANSACTIONS_PAGE_SIZE) {
        setLocalHasMoreTransactions(false);
      } else {
        setLocalLastTransactionDoc(snapshot.docs[snapshot.docs.length - 1]);
      }
    } catch (error) {
      console.error("Error loading more transactions:", error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [workspaceId, loadingMore, hasMoreTransactions, lastTransactionDoc, localHasMoreTransactions, localLastTransactionDoc]);
  
  // Initialize lastTransactionDoc from hook (for first loadMore)
  useEffect(() => {
    if (lastTransactionDoc && !localLastTransactionDoc) {
      setLocalLastTransactionDoc(lastTransactionDoc);
    }
    // Do NOT reset localHasMoreTransactions from hasMoreTransactions - that would undo
    // "end of list" when loadMore returned a partial page, causing infinite loading
  }, [lastTransactionDoc, localLastTransactionDoc]);

  // Reset pagination when workspace changes
  useEffect(() => {
    setPaginatedTransactions([]);
    setLocalHasMoreTransactions(true);
    setLocalLastTransactionDoc(null);
  }, [workspaceId]);

  // Auto-load more when displayed count reaches filtered count
  useEffect(() => {
    if (!isFullyLoaded || loadingMore) return;
    
    const filteredCount = filteredTransactionsLength ?? allTransactions.length;
    const hasMore = localHasMoreTransactions && hasMoreTransactions;
    const docToUse = localLastTransactionDoc || lastTransactionDoc;
    // If we've displayed all filtered transactions and there are more to load
    if (displayedCount >= filteredCount && hasMore && docToUse) {
      loadMoreTransactions();
    }
  }, [isFullyLoaded, displayedCount, filteredTransactionsLength, allTransactions.length, hasMoreTransactions, localHasMoreTransactions, loadingMore, lastTransactionDoc, localLastTransactionDoc, loadMoreTransactions]);

  // Infinite scroll handler
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || !isFullyLoaded) return;

    const handleScroll = (): void => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Load more when user scrolls within 300px of bottom (increased threshold)
      if (scrollHeight - scrollTop - clientHeight < 300) {
        const filteredCount = filteredTransactionsLength ?? allTransactions.length;
        if (displayedCount < filteredCount) {
          // Show more from already loaded transactions
          setDisplayedCount((prev) => Math.min(prev + TRANSACTIONS_PAGE_SIZE, filteredCount));
        } else if (localHasMoreTransactions && hasMoreTransactions && !loadingMore && lastTransactionDoc) {
          // Need to load more from Firestore
          loadMoreTransactions();
        }
      }
    };

    // Check immediately on mount/update
    handleScroll();

    container.addEventListener("scroll", handleScroll);
    // Also check on resize in case content changes
    window.addEventListener("resize", handleScroll);
    
    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isFullyLoaded, displayedCount, filteredTransactionsLength, allTransactions.length, localHasMoreTransactions, hasMoreTransactions, loadingMore, lastTransactionDoc, loadMoreTransactions, tableContainerRef]);

  const hasMoreToLoad = localHasMoreTransactions && hasMoreTransactions;

  return {
    allTransactions,
    displayedCount,
    setDisplayedCount,
    loadingMore,
    paginatedTransactions,
    hasMoreToLoad,
  };
};
